import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandKitSource } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  brand_kit_source: BrandKitSource
}

export type SourceType = 'Website' | 'PlainText' | 'SourceMaterialsMediaObject'

interface CommonVariables {
  brandKitId: number
  name?: string
  emoji?: string
  signal?: AbortSignal
}

export type BrandKitSourceType =
  | 'Website'
  | 'ExternalIntegration'
  | 'PlainText'
  | 'AlmanacFile'
  | 'SourceMaterialsMediaObject'

export interface ExistingSourceVariables extends CommonVariables {
  source: {
    type: BrandKitSourceType
    id: number
  }
}

export interface NewSourceVariables extends CommonVariables {
  sourceType: SourceType
  url?: string
  text?: string
  mediaObjectId?: number
  scrapeImages?: boolean
  followLinks?: boolean
}

export type Variables = ExistingSourceVariables | NewSourceVariables

export const createBrandKitSource = async (vars: Variables & { workspaceId: number }) => {
  const { workspaceId, brandKitId, name, emoji } = vars

  const body =
    'source' in vars
      ? { source_id: vars.source.id, source_type: vars.source.type, name, emoji }
      : {
          source_type: vars.sourceType,
          url: vars.url,
          text: vars.text,
          media_object_id: vars.mediaObjectId,
          name,
          emoji,
          scrape_images: vars.scrapeImages,
          follow_links: vars.followLinks,
        }

  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${brandKitId}/sources`, {
    method: 'POST',
    body,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<BrandKitSource, Error, Variables>, 'mutationFn'> {}

export const useCreateBrandKitSource = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<BrandKitSource, Error, Variables>(async (vars) => {
    const result = await createBrandKitSource({ workspaceId, ...vars })
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])
    return result.brand_kit_source
  }, options)
}
